import { injectScript } from '../utils/utils';
import * as config from '../utils/ima/config';
import { PLAYER_VIDEO_ID } from '../constants';
import { IMA_CONTAINER_ID } from './constants';
import { AdType, } from '../../player/types';
import { ErrorCode } from '../../errors/enums/error-code';
import { ImaActions, } from './actions';
import { loadAndParsePauseVAST } from '../vast/parser';
import { AdsManagerEventKey, } from '../../ads/ads-manager';
import { ImaAdsManagerAdapter } from '../../ads/ima/ima-ads-manager-adapter';
let adsLoader = null;
export const bindAdsManager = (adsManager, dispatcher, adsCompleted, contentPauseRequested, contentResumeRequested) => {
    adsManager.addEventListener(AdsManagerEventKey.ALL_ADS_COMPLETED, () => {
        adsCompleted();
    });
    adsManager.addEventListener(AdsManagerEventKey.COMPLETE, (data) => {
        dispatcher.dispatchAdEnded(data);
    });
    adsManager.addEventListener(AdsManagerEventKey.AD_METADATA, (data) => {
        dispatcher.dispatchAdMetadata(data);
    });
    adsManager.addEventListener(AdsManagerEventKey.LOADED, (event) => {
        dispatcher.dispatchAdLoaded(event);
    });
    adsManager.addEventListener(AdsManagerEventKey.CONTENT_PAUSE_REQUESTED, () => {
        contentPauseRequested();
    });
    adsManager.addEventListener(AdsManagerEventKey.CONTENT_RESUME_REQUESTED, () => {
        contentResumeRequested();
    });
    adsManager.addEventListener(AdsManagerEventKey.PAUSED, (event) => {
        dispatcher.dispatchAdPaused(event);
    });
    adsManager.addEventListener(AdsManagerEventKey.RESUMED, (event) => {
        dispatcher.dispatchAdResumed(event);
    });
    adsManager.addEventListener(AdsManagerEventKey.STARTED, (event) => {
        dispatcher.dispatchAdStarted(event);
    });
    adsManager.addEventListener(AdsManagerEventKey.AD_PROGRESS, (event) => {
        dispatcher.dispatchAdPositionChanged?.(event);
    });
    adsManager.addEventListener(AdsManagerEventKey.CLICK, () => {
        adsManager?.pause();
    });
    adsManager.addEventListener(AdsManagerEventKey.SKIPPED, (event) => {
        dispatcher.dispatchAdSkipped(event);
    });
    adsManager.addEventListener(AdsManagerEventKey.FIRST_QUARTILE, (event) => {
        dispatcher.dispatchAdFirstQuartile(event);
    });
    adsManager.addEventListener(AdsManagerEventKey.MIDPOINT, (event) => {
        dispatcher.dispatchAdMidpoint(event);
    });
    adsManager.addEventListener(AdsManagerEventKey.THIRD_QUARTILE, (event) => {
        dispatcher.dispatchAdThirdQuartile(event);
    });
};
/**
 * Listen to window resize event and resize adsManager
 */
export const WithImaSdk = (Base) => {
    return class AdapterWithImaSdk extends Base {
        actions = ImaActions.getInstance();
        locale = '';
        waitImaSdkLoaded = null;
        container = null;
        videoElement = null;
        adDisplayContainer = null;
        adsManager = null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        constructor(...args) {
            super(...args);
            this.resizeAds = this.resizeAds.bind(this);
            // TS2545: A mixin class must have a constructor
            // with a single rest parameter of type 'any[]'.
            //
            // As a solution we are doing this terrible hack
            // with extracting PlayerOptions from first args
            // element... :existence-is-pain:
            const options = args[0];
            this.waitImaSdkLoaded = this.imaLoadSdk(options.locale);
        }
        pauseRequested(ad) {
            this.dispatchContentPauseRequested();
            this.dispatchAdBreakStarted({
                adType: ad.type,
                isChannelAd: ad.isChannelAd,
                maxDuration: ad?.maxDuration,
            });
        }
        async resumeRequested(ad) {
            this.dispatchAdBreakEnded({
                adType: ad.type,
                isChannelAd: ad.isChannelAd,
                maxDuration: ad?.maxDuration,
            });
            await this.dispatchContentResumeRequested();
        }
        dispatchContentPauseRequested() {
            this.actions.saveState();
            super.dispatchContentPauseRequested();
        }
        async dispatchContentResumeRequested() {
            await this.actions.restoreState();
            super.dispatchContentResumeRequested();
        }
        async imaLoadSdk(locale = '') {
            if (!window.google?.ima) {
                await injectScript({
                    src: 'https://imasdk.googleapis.com/js/sdkloader/ima3.js',
                });
            }
            this.locale = locale;
            if (!window.google?.ima) {
                return Promise.reject(new Error('Google ima is not loaded'));
            }
            return Promise.resolve();
        }
        setView(view) {
            super.setView(view);
            (async () => {
                try {
                    await this.waitImaSdkLoaded;
                    let imaContainer = document.getElementById(IMA_CONTAINER_ID);
                    if (!imaContainer) {
                        imaContainer = document.createElement('div');
                        imaContainer.setAttribute('id', IMA_CONTAINER_ID);
                        imaContainer.style.position = 'absolute';
                        imaContainer.style.top = '0';
                        imaContainer.style.left = '0';
                        imaContainer.style.width = '100%';
                        this.getView()?.appendChild(imaContainer);
                    }
                    this.imaBoot({
                        container: imaContainer,
                        videoElement: document.getElementById(PLAYER_VIDEO_ID),
                        locale: this.locale,
                    });
                }
                catch (error) {
                    this.dispatchAdError({
                        adType: AdType.UNKNOWN,
                        error: {
                            code: ErrorCode.Ad,
                            message: `Could not initialize ima ${error instanceof Error ? error.message : ''}`,
                        },
                    });
                }
            })();
        }
        addResizeListener() {
            window.addEventListener('resize', this.resizeAds);
        }
        cleanupResizeListener() {
            window.removeEventListener('resize', this.resizeAds);
        }
        resizeAds() {
            if (!this.adsManager) {
                return;
            }
            const element = this.videoElement ?? this.container;
            if (!element) {
                return;
            }
            const width = element?.clientWidth;
            const height = element?.clientHeight;
            this.adsManager.resize(width, height);
        }
        imaBoot({ container, videoElement, locale, }) {
            this.container = container;
            this.videoElement = videoElement;
            google.ima.settings.setLocale(locale);
            if (config.supportSkippableAds) {
                google.ima.settings.setDisableCustomPlaybackForIOS10Plus(true);
            }
            if (!videoElement) {
                this.adDisplayContainer = new google.ima.AdDisplayContainer(container);
            }
            else {
                this.adDisplayContainer = new google.ima.AdDisplayContainer(container, videoElement);
            }
            adsLoader = new google.ima.AdsLoader(this.adDisplayContainer);
            /** TIP: Must be done as the result of a user action */
            this.adDisplayContainer.initialize();
            const onAdsManagerLoaded = (event) => {
                const currentAd = event.getUserRequestContext();
                const adsRenderingSettings = new google.ima.AdsRenderingSettings();
                adsRenderingSettings.bitrate = config.maxBitrate;
                adsRenderingSettings.useStyledLinearAds = false; // use mode with play/pause on player
                // disable all UI elements (no, skip button will still be there)
                adsRenderingSettings.uiElements = [];
                if (!videoElement) {
                    this.dispatchAdError({
                        adType: currentAd.type,
                        error: {
                            code: ErrorCode.Ad,
                            message: 'Could not find video element',
                        },
                    });
                    return;
                }
                const imaAdsManager = event.getAdsManager(videoElement, adsRenderingSettings);
                const adsManager = new ImaAdsManagerAdapter(imaAdsManager, currentAd.type);
                this.adsManager = adsManager;
                bindAdsManager(adsManager, this, () => {
                    this.cleanupAdsManager();
                }, () => {
                    this.addResizeListener();
                    this.pauseRequested(currentAd);
                }, async () => {
                    this.cleanupResizeListener();
                    await this.resumeRequested(currentAd);
                });
                try {
                    const element = videoElement ?? container;
                    imaAdsManager.init(element.clientWidth, element.clientHeight);
                    imaAdsManager.start();
                }
                catch (adError) {
                    this.dispatchAdError({
                        adType: currentAd.type,
                        error: {
                            code: ErrorCode.Ad,
                            message: adError instanceof Error ? adError.message : 'IMA Play ads failed',
                        },
                    });
                }
            };
            const onAdsError = (event) => {
                const error = event.getError();
                const currentAd = event.getUserRequestContext();
                this.cleanupAdsManager();
                if (!error) {
                    return;
                }
                this.dispatchAdError({
                    adType: currentAd.type,
                    error: {
                        code: error.getErrorCode(),
                        message: error.getMessage(),
                    },
                });
            };
            adsLoader.addEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onAdsManagerLoaded);
            adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onAdsError);
        }
        async loadPauseVast(pauseAdUrl) {
            try {
                const pauseAdData = await loadAndParsePauseVAST(pauseAdUrl);
                this.dispatchPauseAdParsed(pauseAdData);
            }
            catch (error) {
                this.dispatchAdError({
                    adType: AdType.PAUSE,
                    error: {
                        code: ErrorCode.Ad,
                        message: error instanceof Error ? error.message : 'Failed to load pause ad VAST',
                    },
                });
            }
        }
        requestVast(ad) {
            /**
             * When Ad requested while another Ad is playing
             * we must destroy currently playing instance, IMA can't handle it
             */
            this.cleanupAdsManager();
            if (!adsLoader) {
                this.dispatchAdError({
                    adType: ad.type,
                    error: {
                        code: ErrorCode.Ad,
                        message: 'AdsLoader is not initialized',
                    },
                });
                return;
            }
            // Request video ads
            const adsRequest = new google.ima.AdsRequest();
            // Add vast url to request
            adsRequest.adTagUrl = ad.vastUrl;
            // Trigger onAdsManagerLoaded
            adsLoader.requestAds(adsRequest, ad);
        }
        loadVast(ad) {
            this.requestVast(ad);
        }
        loadFastForwardVast(ad) {
            this.requestVast(ad);
        }
        imaSetVolume(volume) {
            this.adsManager?.setVolume(volume);
        }
        imaPause() {
            this.adsManager?.pause();
        }
        imaPlay() {
            this.adsManager?.resume();
        }
        imaStop() {
            this.cleanupAdsManager();
        }
        cleanupAdsManager() {
            this.cleanupResizeListener();
            this.adsManager?.destroy();
            this.adsManager = null;
            adsLoader?.contentComplete();
        }
    };
};
